import React, { useState, useEffect } from 'react';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import type { ImportPreview, ImportResult, ImportStrategy } from '@/types/backup';
import { env } from '@/lib/env';
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { fetchConversationsSnapshot } from '@/features/conversations/lib/conversationAdapter';
import { renderConversationIcon } from './ConversationIcon';
import { getModuleIcon } from './SelectiveExportModuleIcon';

interface SelectiveExportImportPanelProps {
  onClose: () => void;
}

interface ConversationWithModules extends Conversation {
  modules?: Module[];
}

export const SelectiveExportImportPanel: React.FC<SelectiveExportImportPanelProps> = ({ onClose }) => {
  const { token: authToken, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('import');
  const [conversations, setConversations] = useState<ConversationWithModules[]>([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [expandedConversationIds, setExpandedConversationIds] = useState<string[]>([]);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Import state
  const [importType, setImportType] = useState<'full' | 'selective'>('selective');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [strategy, setStrategy] = useState<ImportStrategy>('merge');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);

  useEffect(() => {
    if (authToken && workspaceId) {
      fetchConversations();
    }
  }, [authToken, workspaceId]);

  const fetchConversations = async () => {
    if (!authToken || !workspaceId) {
      setError('Not authenticated. Please log in.');
      return;
    }

    try {
      const response = await fetchConversationsSnapshot(authToken, workspaceId, {
        limit: 200,
      });
      const nonArchivedConversations = response.conversations
        .filter(conversation => !conversation.archivedAt)
        .map(conversation => ({
          ...conversation,
          modules: conversation.modules?.filter(module => !module.archived)
        }));
      setConversations(nonArchivedConversations);
      setExpandedConversationIds([]);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Make sure the backend is running.');
    }
  };

  const handleSelectiveExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      // Build query params based on selected modules
      const queryParams = new URLSearchParams();

      if (selectedModuleIds.length > 0) {
        selectedModuleIds.forEach(id => queryParams.append('moduleIds', id));
      }

      if (includeFiles !== undefined) {
        queryParams.append('includeFiles', String(includeFiles));
      }

      const response = await fetch(`${env.apiBaseUrl}/backup/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'selective-backup.zip';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFullExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const response = await fetch(`${env.apiBaseUrl}/backup/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'backup.zip';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Download the ZIP file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      setError('Please select a file');
      return;
    }

    setIsPreviewing(true);
    setError(null);
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('strategy', strategy);

      const response = await fetch(`${env.apiBaseUrl}/backup/preview-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const result: ImportPreview = await response.json();

      if (!result.ok) {
        throw new Error('Preview failed');
      }

      setPreview(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!importFile || !preview) {
      setError('Please preview the import first');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('strategy', strategy);
      // conflictMode defaults to 'rename' on backend

      const response = await fetch(`${env.apiBaseUrl}/backup/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const result: ImportResult = await response.json();

      if (!result.ok) {
        throw new Error('Import failed');
      }

      setImportResult(result);
      setPreview(null);
      setImportFile(null);

      // For full restore, keep isImporting true and reload immediately
      // For merge, show success message briefly
      if (strategy === 'replace') {
        // Don't set isImporting to false - keep spinner running
        window.location.reload();
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      // Only set isImporting to false for merge strategy
      // For replace, we want to keep the spinner running until page reloads
      if (strategy !== 'replace') {
        setIsImporting(false);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setPreview(null);
      setError(null);
    }
  };

  const toggleConversationExpanded = (conversationId: string) => {
    setExpandedConversationIds(prev =>
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]
    );
  };

  const selectAllModulesInConversation = (conversation: ConversationWithModules) => {
    const conversationModuleIds = conversation.modules?.map(m => m.id) || [];
    const allSelected = conversationModuleIds.every(id => selectedModuleIds.includes(id));

    if (allSelected) {
      // Deselect all modules in this conversation
      setSelectedModuleIds(prev => prev.filter(id => !conversationModuleIds.includes(id)));
    } else {
      // Select all modules in this conversation
      setSelectedModuleIds(prev => [...new Set([...prev, ...conversationModuleIds])]);
    }
  };

  const isConversationFullySelected = (conversation: ConversationWithModules) => {
    const conversationModuleIds = conversation.modules?.map(m => m.id) || [];
    return (
      conversationModuleIds.length > 0 &&
      conversationModuleIds.every(id => selectedModuleIds.includes(id))
    );
  };

  const selectAllModules = () => {
    const allModuleIds = conversations.flatMap((conversation) => conversation.modules?.map(m => m.id) || []);
    const allSelected = allModuleIds.length > 0 && allModuleIds.every(id => selectedModuleIds.includes(id));

    if (allSelected) {
      setSelectedModuleIds([]);
    } else {
      setSelectedModuleIds(allModuleIds);
    }
  };

  const areAllModulesSelected = () => {
    const allModuleIds = conversations.flatMap((conversation) => conversation.modules?.map(m => m.id) || []);
    return allModuleIds.length > 0 && allModuleIds.every(id => selectedModuleIds.includes(id));
  };

  const isConversationPartiallySelected = (conversation: ConversationWithModules) => {
    const conversationModuleIds = conversation.modules?.map(m => m.id) || [];
    return (
      conversationModuleIds.some(id => selectedModuleIds.includes(id)) &&
      !isConversationFullySelected(conversation)
    );
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="selective-panel">
      <div className="modal-tabs">
        <button
          className={`modal-tab ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          Import
        </button>
        <button
          className={`modal-tab ${activeTab === 'export' ? 'active' : ''}`}
          onClick={() => setActiveTab('export')}
        >
          Export
        </button>
      </div>


      {activeTab === 'export' ? (
        <div className="panel-section">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            {conversations.length === 0 ? (
              <div className="help-text" style={{ padding: '16px', textAlign: 'center', fontSize: '18px', color: 'var(--color-text-muted)', fontWeight: 'normal', lineHeight: 1.4 }}>
                {error ? 'Failed to load conversations' : 'No conversations yet'}
              </div>
            ) : (
              <div className="conversation-module-tree xv-scroll">
                {conversations.map(conversation => (
                  <div key={conversation.id} className="conversation-item">
                    <div className="conversation-header">
                      <input
                        type="checkbox"
                        checked={isConversationFullySelected(conversation)}
                        ref={input => {
                          if (input) {
                            input.indeterminate = isConversationPartiallySelected(conversation);
                          }
                        }}
                        onChange={() => selectAllModulesInConversation(conversation)}
                      />
                      <div
                        className="conversation-name"
                        onClick={() => toggleConversationExpanded(conversation.id)}
                        style={{ cursor: 'pointer', flex: 1 }}
                      >
                        {conversation.icon && <span className="conversation-icon">{renderConversationIcon(conversation.icon)}</span>}
                        {conversation.name}
                        <span className="module-count">
                          ({conversation.modules?.length || 0} modules)
                        </span>
                      </div>
                      <button
                        className="expand-button"
                        onClick={() => toggleConversationExpanded(conversation.id)}
                      >
                        {expandedConversationIds.includes(conversation.id) ? '▼' : '▶'}
                      </button>
                    </div>

                    {expandedConversationIds.includes(conversation.id) && (
                      <div className="module-list">
                        {conversation.modules && conversation.modules.length > 0 ? (
                          conversation.modules.map(module => (
                            <label key={module.id} className="checkbox-label module-item">
                              <input
                                type="checkbox"
                                checked={selectedModuleIds.includes(module.id)}
                                onChange={() => toggleModule(module.id)}
                              />
                              {getModuleIcon(module.type)}
                              <span>
                                {module.label || module.type}
                              </span>
                            </label>
                          ))
                        ) : (
                          <div className="no-modules">No modules in this conversation</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button
              onClick={selectAllModules}
              className="btn btn-secondary"
              style={{ flex: 1 }}
            >
              {areAllModulesSelected() ? 'Unselect All' : 'Select All'}
            </button>
            <button
              onClick={handleSelectiveExport}
              disabled={isExporting || selectedModuleIds.length === 0}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              {isExporting ? 'Exporting...' : 'Download Export'}
            </button>
          </div>
        </div>
      ) : (
        <div className="panel-section">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  value="merge"
                  checked={strategy === 'merge'}
                  onChange={(e) => setStrategy(e.target.value as ImportStrategy)}
                />
                <div className="radio-content">
                  <div className="radio-title">Merge</div>
                  <div className="radio-description">Add new content to existing data</div>
                </div>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  value="replace"
                  checked={strategy === 'replace'}
                  onChange={(e) => setStrategy(e.target.value as ImportStrategy)}
                />
                <div className="radio-content">
                  <div className="radio-title">Full Restore</div>
                  <div className="radio-description">Delete all current data and restore from backup</div>
                </div>
              </label>
            </div>
          </div>

          {importFile && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
            }}>
              {/* File icon */}
              <div style={{
                flexShrink: 0,
                width: '40px',
                height: '40px',
                background: 'var(--color-surface-hover)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8"></polyline>
                  <rect x="1" y="3" width="22" height="5"></rect>
                  <line x1="10" y1="12" x2="14" y2="12"></line>
                </svg>
              </div>

              {/* File info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  margin: '0 0 4px 0',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {importFile.name}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                  <p style={{ color: 'var(--color-text-muted)', margin: 0, fontWeight: 500 }}>ZIP</p>
                  <span style={{ color: 'var(--color-border-strong)' }}>•</span>
                  <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                    {(importFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>
          )}

          {preview && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '6px',
            }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--color-text)' }}>Preview</h4>
              <div className="preview-stats">
                <div className="stat-item" style={{ background: 'var(--color-surface-hover)' }}>
                  <span className="stat-label">Conversations:</span>
                  <span className="stat-value">{preview.entityCounts.conversations ?? 0}</span>
                </div>
                <div className="stat-item" style={{ background: 'var(--color-surface-hover)' }}>
                  <span className="stat-label">Objects:</span>
                  <span className="stat-value">{preview.entityCounts.objects}</span>
                </div>
                <div className="stat-item" style={{ background: 'var(--color-surface-hover)' }}>
                  <span className="stat-label">Assets:</span>
                  <span className="stat-value">{preview.entityCounts.files}</span>
                </div>
              </div>

              {strategy === 'merge' && preview.conflicts.length > 0 && (
                <div className="conflicts-section">
                  <h5>Conflicts Detected</h5>
                  <ul className="conflicts-list">
                    {preview.conflicts.map((conflict, idx) => (
                      <li key={idx} className="conflict-item">
                        <strong>{conflict.name}</strong> - {conflict.reason}
                      </li>
                    ))}
                  </ul>
                  <p className="help-text">
                    Conflicting conversations will be imported with an "(import)" suffix
                  </p>
                </div>
              )}
            </div>
          )}

          {strategy === 'replace' && preview && (
            <div className="warning-box" style={{ marginTop: '6px' }}>
              <div>
                <strong>Warning: This will DELETE ALL your current data!</strong>
                <p>
                  This operation will completely replace your entire database with the backup file.
                  All current conversations, objects, sessions, and messages will be permanently deleted.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', marginBottom: '8px' }}>
                  <label className="checkbox-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={acknowledgedWarning}
                      onChange={(e) => setAcknowledgedWarning(e.target.checked)}
                      className="warning-checkbox"
                      style={{
                        cursor: 'pointer',
                        margin: 0,
                        accentColor: acknowledgedWarning ? 'var(--color-danger)' : undefined,
                        width: '18px',
                        height: '18px'
                      }}
                    />
                    <span style={{ fontWeight: 900, color: 'var(--color-danger-text)', fontSize: '14px' }}>I understand</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <label className="btn btn-secondary" style={{ flex: 1, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              {importFile ? 'Change File' : 'Choose File'}
              <input
                type="file"
                accept=".zip"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={preview ? handleExecuteImport : handlePreviewImport}
              disabled={(preview ? isImporting : isPreviewing) || !importFile || (strategy === 'replace' && !!preview && !acknowledgedWarning)}
              className={strategy === 'replace' ? 'btn btn-danger' : 'btn btn-primary'}
              style={{
                flex: 1,
                opacity: (isImporting || !importFile || isPreviewing || (strategy === 'replace' && !!preview && !acknowledgedWarning))
                  ? (strategy === 'replace' ? 0.5 : 0.75)
                  : 1,
                cursor: (isImporting || !importFile || isPreviewing || (strategy === 'replace' && !!preview && !acknowledgedWarning)) ? 'not-allowed' : 'pointer'
              }}
            >
              {preview
                ? (isImporting ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                    </div>
                  ) : 'Execute Import')
                : (isPreviewing ? 'Loading Preview...' : 'Preview Import')
              }
            </button>
          </div>

          {importResult && (
            <div className="success-message">
              <h4>Import Successful!</h4>
              <p>Page will reload shortly...</p>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};
